import { Button, Typography } from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { useBlurbContext } from '../../context/Blurb.context';
import { useCheckoutContext } from '../../context/checkout.context';
import { useSettingsHook } from '../../hooks/settingsStorageHook';
import { testId } from '../../utils';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import clsx from 'clsx';
import React from 'react';

export const ShippingOptions = () => {
  const {
    checkoutData,
    setShippingTier,
    checkoutIsLoading,
    setCheckoutState,
    errors,
  } = useCheckoutContext();
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });

  const onContinue = React.useCallback(() => {
    setCheckoutState('PAYMENT');
  }, [setCheckoutState]);

  if (!checkoutData) {
    return null;
  }

  const { shippingOptionsPriced, shippingTier } = checkoutData;

  return (
    <div className="flex flex-col gap-4 pt-4">
      {errors.shippingTier && (
        <Alert {...testId('shipping-options', 'error')} variant="error">
          {t('checkout.shipping_option_error')}
        </Alert>
      )}
      {shippingOptionsPriced.map(
        (option: Cornerstone.CheckoutShippingOptionsDto) => (
          <ShippingOption
            key={option.shippingTier}
            active={shippingTier === option.shippingTier}
            shippingTier={option.shippingTier}
            price={option.shippingTierPrice.amount}
            currency={option.shippingTierPrice.currency}
            estimatedDeliveryDateUTC={option.estimatedDeliveryDateUTC}
            onSelect={() => setShippingTier(option.shippingTier)}
          />
        ),
      )}
      <div>
        <Button
          {...testId('shipping-options', 'continue')}
          size="sm"
          disabled={checkoutIsLoading}
          onClick={onContinue}
        >
          {t('auth.continue')}
        </Button>
      </div>
    </div>
  );
};

const ShippingOption = ({
  active,
  shippingTier,
  price,
  currency,
  estimatedDeliveryDateUTC,
  onSelect,
}: {
  active?: boolean;
  shippingTier: string;
  price: number;
  currency: Cornerstone.MonetaryAmountCurrency;
  estimatedDeliveryDateUTC: string;

  onSelect: () => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });
  const { settings } = useSettingsHook();
  const { currencyFormatter } = useBlurbContext();

  const date = new Date(estimatedDeliveryDateUTC);

  const monthDay = date.toLocaleDateString(settings?.locale ?? 'en-US', {
    month: 'long',
    day: 'numeric',
  });

  return (
    <button
      {...testId('shipping-options', shippingTier)}
      className={clsx(
        active ? 'border-blue-active' : 'border-gray-light',
        'rounded-md border-2 border-solid p-2',
      )}
      onClick={onSelect}
      data-status={active ? 'active' : 'inactive'}
    >
      <div className="flex align-center ">
        <Typography variant="body-semibold">{shippingTier}</Typography>
        <hr
          className="mx-1 my-2 border-gray-dark w-full"
          style={{ border: '2px dotted' }}
        ></hr>
        <Typography variant="body-semibold">
          {currencyFormatter({ amount: price, currency: currency })}
        </Typography>
      </div>
      <Typography variant="body">
        {`${t('arriving_by')} ${monthDay}`}
      </Typography>
    </button>
  );
};
